import React, { useState } from 'react';
import { OverallSalesHistoryRow } from '@/types/asinSalesHistory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

interface OverallLeaderboardProps {
  data: OverallSalesHistoryRow[];
  loading: boolean;
}

type SortField = 'date' | 'traffic' | 'conversionRate' | 'units' | 'revenue' | 'ppcSpend' | 'grossProfit';
type SortDirection = 'asc' | 'desc';

export const OverallLeaderboard: React.FC<OverallLeaderboardProps> = ({ data, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('grossProfit');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
  };

  // Filter and sort raw daily data (exactly like Google Sheet)
  const filteredAndSortedData = React.useMemo(() => {
    let filtered = data;

    // Apply search filter - search by date
    if (searchTerm) {
      filtered = filtered.filter(row =>
        formatDate(row.date).toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.date.toISOString().split('T')[0].includes(searchTerm)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'traffic':
          aValue = a.traffic;
          bValue = b.traffic;
          break;
        case 'conversionRate':
          aValue = a.conversionRate;
          bValue = b.conversionRate;
          break;
        case 'units':
          aValue = a.units;
          bValue = b.units;
          break;
        case 'revenue':
          aValue = a.revenue;
          bValue = b.revenue;
          break;
        case 'ppcSpend':
          aValue = a.ppcSpend;
          bValue = b.ppcSpend;
          break;
        case 'grossProfit':
          aValue = a.grossProfit;
          bValue = b.grossProfit;
          break;
        default:
          aValue = a.grossProfit;
          bValue = b.grossProfit;
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [data, searchTerm, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3" />
      : <ArrowDown className="h-3 w-3" />;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sales & Profit History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex space-x-2">
              <div className="w-64 h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle>Sales & Profit History</CardTitle>
          <div className="text-sm text-muted-foreground">
            {filteredAndSortedData.length} records
          </div>
        </div>
        
        {/* Search Filter */}
        <div className="flex items-center space-x-4 mt-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search dates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('date')}
                    className="h-6 p-0 hover:bg-transparent"
                  >
                    Date <SortIcon field="date" />
                  </Button>
                </th>
                <th className="text-right py-2 px-3 font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('traffic')}
                    className="h-6 p-0 hover:bg-transparent"
                  >
                    Traffic <SortIcon field="traffic" />
                  </Button>
                </th>
                <th className="text-right py-2 px-3 font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('conversionRate')}
                    className="h-6 p-0 hover:bg-transparent"
                  >
                    Conversion Rate <SortIcon field="conversionRate" />
                  </Button>
                </th>
                <th className="text-right py-2 px-3 font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('units')}
                    className="h-6 p-0 hover:bg-transparent"
                  >
                    Units <SortIcon field="units" />
                  </Button>
                </th>
                <th className="text-right py-2 px-3 font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('revenue')}
                    className="h-6 p-0 hover:bg-transparent"
                  >
                    Revenue <SortIcon field="revenue" />
                  </Button>
                </th>
                <th className="text-right py-2 px-3 font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('ppcSpend')}
                    className="h-6 p-0 hover:bg-transparent"
                  >
                    PPC Spend <SortIcon field="ppcSpend" />
                  </Button>
                </th>
                <th className="text-right py-2 px-3 font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('grossProfit')}
                    className="h-6 p-0 hover:bg-transparent"
                  >
                    Gross Profit <SortIcon field="grossProfit" />
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.slice(0, 50).map((row, index) => (
                <tr key={`${row.date.toISOString()}-${index}`} className="border-b hover:bg-muted/50">
                  <td className="py-2 px-3 text-muted-foreground text-xs">
                    {formatDate(new Date(row.date))}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {row.traffic.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {formatPercent(row.conversionRate)}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {row.units}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {row.revenue > 0 ? formatCurrency(row.revenue) : '$0'}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {row.ppcSpend > 0 ? formatCurrency(row.ppcSpend) : '$0'}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={row.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {row.grossProfit !== 0 ? formatCurrency(row.grossProfit) : '$0'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredAndSortedData.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No records found matching your criteria
            </div>
          )}
          
          {filteredAndSortedData.length > 50 && (
            <div className="text-center py-4 text-muted-foreground text-sm">
              Showing first 50 records of {filteredAndSortedData.length} total
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};